import React, { useState, useMemo } from 'react';
import { GraduationCap, Calendar, Award, Clock, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * TrainingHistoryCard - Displays crew member's training history
 * Groups training sessions by category with recent activity summary
 */
const TrainingHistoryCard = ({ trainingRecords, crewId, onViewDetails }) => {
  // Collapsed state for progressive disclosure
  const [isExpanded, setIsExpanded] = useState(false);

  // Process training records for this crew member
  const processedTraining = useMemo(() => {
    if (!trainingRecords || !crewId) return { sessions: [], byCategory: {}, stats: {} };

    // Filter to only sessions this crew member participated in
    const crewSessions = trainingRecords.filter(session =>
      session.trainees?.some(t => t.crewId === crewId)
    );

    // Group by category
    const byCategory = crewSessions.reduce((acc, session) => {
      const category = session.category || 'General';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(session);
      return acc;
    }, {});

    // Sort sessions within each category by date (most recent first)
    Object.keys(byCategory).forEach(category => {
      byCategory[category].sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );
    });

    // Calculate stats
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

    const recentSessions = crewSessions.filter(s => new Date(s.date) >= thirtyDaysAgo);
    const last90Days = crewSessions.filter(s => new Date(s.date) >= ninetyDaysAgo);

    // Count certificates issued
    const certificatesIssued = crewSessions.reduce((count, session) => {
      const certs = session.certificatesIssued?.filter(c => c.crewId === crewId) || [];
      return count + certs.length;
    }, 0);

    // Calculate total training hours
    const totalHours = crewSessions.reduce((sum, session) => {
      return sum + (parseFloat(session.duration) || 0);
    }, 0);

    return {
      sessions: crewSessions,
      byCategory,
      stats: {
        total: crewSessions.length,
        last30Days: recentSessions.length,
        last90Days: last90Days.length,
        certificatesIssued,
        totalHours: totalHours.toFixed(1),
        categories: Object.keys(byCategory).length
      }
    };
  }, [trainingRecords, crewId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const { sessions, byCategory, stats } = processedTraining;

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      {/* Header - Inside Card */}
      <div className="bg-slate-700 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-white" />
            <h3 className="text-base font-semibold text-white">Training History</h3>
          </div>
          {stats.total > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
              {stats.total} session{stats.total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      <div className="px-6 py-5">
        {sessions.length > 0 ? (
          <div className="space-y-4">
            {/* Stats Summary - Always visible */}
            <div className="grid grid-cols-2 gap-3 pb-3 border-b border-gray-100">
              <div className="text-center p-2 bg-gray-50 rounded-md">
                <p className="text-lg font-semibold text-gray-900">{stats.totalHours}</p>
                <p className="text-xs text-gray-500">Total Hours</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-md">
                <p className="text-lg font-semibold text-gray-900">{stats.certificatesIssued}</p>
                <p className="text-xs text-gray-500">Certificates</p>
              </div>
            </div>

            {/* Expandable Content */}
            {isExpanded && (
              <>
                {/* Recent Activity */}
                {stats.last30Days > 0 && (
                  <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 px-2 py-1.5 rounded-md">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{stats.last30Days} session{stats.last30Days !== 1 ? 's' : ''} in last 30 days</span>
                  </div>
                )}

                {/* Training by Category */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">By Category</p>
                  {Object.entries(byCategory).slice(0, 4).map(([category, categorySessions]) => {
                    const latestSession = categorySessions[0];
                    const hasCert = categorySessions.some(s =>
                      s.certificatesIssued?.some(c => c.crewId === crewId)
                    );

                    return (
                      <div
                        key={category}
                        className="flex items-center justify-between py-2 px-2 bg-gray-50 rounded-md"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate">{category}</p>
                            {hasCert && (
                              <Award className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" title="Certificate issued" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Last: {formatDate(latestSession?.date)}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded">
                          {categorySessions.length}
                        </span>
                      </div>
                    );
                  })}

                  {Object.keys(byCategory).length > 4 && (
                    <p className="text-xs text-gray-500 text-center">
                      +{Object.keys(byCategory).length - 4} more categories
                    </p>
                  )}
                </div>

                {/* View Details Button */}
                {onViewDetails && (
                  <button
                    onClick={onViewDetails}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                  >
                    View Full Training History
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </>
            )}

            {/* Expand/Collapse Toggle */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex items-center justify-center gap-1 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show more
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="text-center py-4">
            <GraduationCap className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 italic text-sm">No training records found</p>
            <p className="text-xs text-gray-400 mt-1">Training sessions will appear here once recorded</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingHistoryCard;
